import React, { useCallback, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import axios from 'axios';

interface MaterialAssignment {
  crown?: {
    height: number;
    radius: number;
    quantity: number;
    type: 'new' | 'reuse';
  };
  left_wing?: {
    width: number;
    radius: number;
    quantity: number;
    type: 'new' | 'reuse';
  };
  right_wing?: {
    width: number;
    radius: number;
    quantity: number;
    type: 'new' | 'reuse';
  };
  column?: {
    height: number;
    thickness: string;
    quantity: number;
    type: 'new' | 'reuse';
  };
  upper?: {
    width: number;
    thickness: string;
    quantity: number;
    type: 'new' | 'reuse';
  };
  lower_beam?: {
    length: number;
    thickness: string;
    quantity: number;
    type: 'new' | 'reuse';
  };
  upper_beam?: {
    length: number;
    thickness: string;
    quantity: number;
    type: 'new' | 'reuse';
  };
  reuseMaterials?: Record<string, Array<{ id: number; quantity: number }>>;
}

interface ReuseMaterial {
  id: number;
  company_name: string;
  site_name: string;
  height?: number;
  width?: number;
  length?: number;
  radius?: number;
  thickness?: number;
  quantity: number;
  received_date: string;
  status: string;
  material_type: 'crown' | 'left_wing' | 'right_wing' | 'column' | 'upper' | 'upper_beam' | 'lower_beam';
  materials: {
    [key: string]: {
      height?: number;
      width?: number;
      length?: number;
      radius?: number;
      thickness?: number;
      quantity: number;
      type: 'new' | 'reuse';
    };
  };
}

interface ReuseMaterialWithDetails extends ReuseMaterial {
  original_quantity: number;
}

interface ReuseMaterialState {
  [key: string]: (ReuseMaterial & { original_quantity?: number })[];
}

interface MaterialInputs {
  crown: {
    height: string;
    radius: string;
    quantity: string;
    type: 'new' | 'reuse';
  };
  left_wing: {
    width: string;
    radius: string;
    quantity: string;
    type: 'new' | 'reuse';
  };
  right_wing: {
    width: string;
    radius: string;
    quantity: string;
    type: 'new' | 'reuse';
  };
  column: {
    height: string;
    thickness: string;
    quantity: string;
    type: 'new' | 'reuse';
  };
  upper: {
    width: string;
    thickness: string;
    quantity: string;
    type: 'new' | 'reuse';
  };
  upper_beam: {
    length: string;
    thickness: string;
    quantity: string;
    type: 'new' | 'reuse';
  };
  lower_beam: {
    length: string;
    thickness: string;
    quantity: string;
    type: 'new' | 'reuse';
  };
}

interface Material {
  type: 'new' | 'reuse';
  material_id?: number;
  quantity: number;
  // ... 기타 필요한 속성들
}

interface Materials {
  crown: Material;
  left_wing: Material;
  right_wing: Material;
  column: Material;
  upper: Material;
  upper_beam: Material;
  lower_beam: Material;
}

interface MaterialAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: MaterialAssignment) => void;
  selectedSale: {
    purpose?: string;
    id: number;
  } | null;
}

export default function MaterialAssignmentDialog({ open, onClose, onSubmit, selectedSale }: MaterialAssignmentDialogProps) {
  const localInputsRef = useRef<MaterialAssignment>({});
  const [reuseDialogOpen, setReuseDialogOpen] = useState(false);
  const [currentMaterialType, setCurrentMaterialType] = useState<keyof ReuseMaterial['materials']>('crown');
  const [reuseMaterials, setReuseMaterials] = useState<ReuseMaterialState>({
    crown: [],
    left_wing: [],
    right_wing: [],
    column: [],
    upper: [],
    upper_beam: [],
    lower_beam: []
  });

  const [inputs, setInputs] = useState<MaterialInputs>({
    crown: { height: '', radius: '', quantity: '', type: 'new' },
    left_wing: { width: '', radius: '', quantity: '', type: 'new' },
    right_wing: { width: '', radius: '', quantity: '', type: 'new' },
    column: { height: '', thickness: '', quantity: '', type: 'new' },
    upper: { width: '', thickness: '', quantity: '', type: 'new' },
    upper_beam: { length: '', thickness: '', quantity: '', type: 'new' },
    lower_beam: { length: '', thickness: '', quantity: '', type: 'new' }
  });

  const { enqueueSnackbar } = useSnackbar();

  const [selectedMaterial, setSelectedMaterial] = useState<any>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleInputChange = (type: keyof MaterialInputs, field: string, value: string) => {
    setInputs(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    if (!selectedSale) {
      alert('매출 항목이 선택되지 않았습니다.');
      return;
    }

    try {
      setError(null);
      setLoading(true);

      // 자재 수량 검증 및 업데이트
      for (const [type, materials] of Object.entries(reuseMaterials)) {
        for (const material of materials) {
          if (material.quantity > 0) {
            try {
              // 자재 수량 검증
              const response = await api.get(
                `/api/inventory/${material.id}/quantity`
              );
              const currentQuantity = response.data.quantity;
              
              if (currentQuantity < material.quantity) {
                throw new Error(`${type} 자재의 수량이 부족합니다. (현재: ${currentQuantity}, 요청: ${material.quantity})`);
              }
              
              // 자재 수량 업데이트
              const updateData = {
                quantity: material.quantity,
                material_type: type,
                is_new_allocation: true
              };
              
              await api.put(
                `/api/inventory/${material.id}/quantity`,
                updateData
              );
            } catch (error) {
              console.error(`${type} 자재의 수량을 업데이트하는 중 오류가 발생했습니다.`, error);
              throw new Error(`${type} 자재의 수량을 업데이트하는 중 오류가 발생했습니다.`);
            }
          }
        }
      }

      // 할당 데이터 저장
      const assignmentData: MaterialAssignment = {
        crown: {
          height: inputs.crown?.height ? parseFloat(inputs.crown.height) : 0,
          radius: inputs.crown?.radius ? parseFloat(inputs.crown.radius) : 0,
          quantity: inputs.crown?.quantity ? parseInt(inputs.crown.quantity) : 0,
          type: inputs.crown?.type || 'new'
        },
        left_wing: {
          width: inputs.left_wing?.width ? parseFloat(inputs.left_wing.width) : 0,
          radius: inputs.left_wing?.radius ? parseFloat(inputs.left_wing.radius) : 0,
          quantity: inputs.left_wing?.quantity ? parseInt(inputs.left_wing.quantity) : 0,
          type: inputs.left_wing?.type || 'new'
        },
        right_wing: {
          width: inputs.right_wing?.width ? parseFloat(inputs.right_wing.width) : 0,
          radius: inputs.right_wing?.radius ? parseFloat(inputs.right_wing.radius) : 0,
          quantity: inputs.right_wing?.quantity ? parseInt(inputs.right_wing.quantity) : 0,
          type: inputs.right_wing?.type || 'new'
        },
        column: {
          height: inputs.column?.height ? parseFloat(inputs.column.height) : 0,
          thickness: inputs.column?.thickness || '',
          quantity: inputs.column?.quantity ? parseInt(inputs.column.quantity) : 0,
          type: inputs.column?.type || 'new'
        },
        upper: {
          width: inputs.upper?.width ? parseFloat(inputs.upper.width) : 0,
          thickness: inputs.upper?.thickness || '',
          quantity: inputs.upper?.quantity ? parseInt(inputs.upper.quantity) : 0,
          type: inputs.upper?.type || 'new'
        },
        upper_beam: {
          length: inputs.upper_beam?.length ? parseFloat(inputs.upper_beam.length) : 0,
          thickness: inputs.upper_beam?.thickness || '',
          quantity: inputs.upper_beam?.quantity ? parseInt(inputs.upper_beam.quantity) : 0,
          type: inputs.upper_beam?.type || 'new'
        }, 
        lower_beam: {
          length: inputs.lower_beam?.length ? parseFloat(inputs.lower_beam.length) : 0,
          thickness: inputs.lower_beam?.thickness || '',
          quantity: inputs.lower_beam?.quantity ? parseInt(inputs.lower_beam.quantity) : 0,
          type: inputs.lower_beam?.type || 'new'
        },
        
        reuseMaterials: Object.entries(reuseMaterials).reduce((acc, [type, materials]) => {
          if (materials.length > 0) {
            acc[type] = materials.map(material => ({
              id: material.id,
              quantity: material.quantity
            }));
          }
          return acc;
        }, {} as Record<string, Array<{ id: number; quantity: number }>>)
      };

      console.log('할당 데이터:', assignmentData);
      await api.post(`/api/sales/${selectedSale.id}/materials`, assignmentData);
      enqueueSnackbar('자재가 할당되었습니다.', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error('할당 데이터 저장 중 오류 발생:', error);
      enqueueSnackbar(error instanceof Error ? error.message : '할당 데이터 저장 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchReuseMaterials = async (type: keyof ReuseMaterial['materials']) => {
    try {
      console.log('재활용 자재 조회 시작:', type);
      const response = await api.get(`/api/inventory`);
      console.log('재활용 자재 조회 결과:', response.data);
      
      // API 응답이 배열인지 확인하고, 배열이 아니면 빈 배열로 초기화
      const materialsArray = Array.isArray(response.data) ? response.data : [];
      
      // materials 속성이 있고 해당 타입의 자재가 있는 항목만 필터링
      const filteredMaterials = materialsArray.filter((item: ReuseMaterial) => 
        item.materials && item.materials[type] && item.materials[type].quantity > 0
      );
      
      console.log('필터링된 재활용 자재:', filteredMaterials);
      
      setReuseMaterials(prev => ({
        ...prev,
        [type]: filteredMaterials
      }));
    } catch (error) {
      console.error('재활용 자재 조회 오류:', error);
      // 에러 발생 시 빈 배열로 초기화
      setReuseMaterials(prev => ({
        ...prev,
        [type]: []
      }));
    }
  };

  const handleReuseButtonClick = (type: keyof ReuseMaterial['materials']) => {
    console.log('재활용 자재 선택 버튼 클릭:', type);
    setCurrentMaterialType(type);
    setReuseDialogOpen(true);
    fetchReuseMaterials(type);
  };

  const handleReuseMaterialSelect = (material: ReuseMaterial) => {
    console.log('선택된 재활용 자재:', material);
    if (currentMaterialType === 'crown') {
      setInputs(prev => ({
        ...prev,
        crown: {
          height: material.materials?.crown?.height?.toString() || '0',
          radius: material.materials?.crown?.radius?.toString() || '0',
          quantity: material.quantity?.toString() || '0',
          type: 'reuse'
        }
      }));
      setReuseMaterials(prev => ({
        ...prev,
        crown: [{
          ...material,
          id: material.id,
          quantity: material.quantity || 0
        }]
      }));
    } else if (currentMaterialType === 'left_wing') {
      setInputs(prev => ({
        ...prev,
        left_wing: {
          width: material.materials?.left_wing?.width?.toString() || '0',
          radius: material.materials?.left_wing?.radius?.toString() || '0',
          quantity: material.quantity?.toString() || '0',
          type: 'reuse'
        }
      }));
      setReuseMaterials(prev => ({
        ...prev,
        left_wing: [{
          ...material,
          id: material.id,
          quantity: material.quantity || 0
        }]
      }));  
    } else if (currentMaterialType === 'right_wing') {
      setInputs(prev => ({
        ...prev,
        right_wing: {
          width: material.materials?.right_wing?.width?.toString() || '0',
          radius: material.materials?.right_wing?.radius?.toString() || '0',
          quantity: material.quantity?.toString() || '0',
          type: 'reuse'
        }
      }));
      setReuseMaterials(prev => ({
        ...prev,
        right_wing: [{
          ...material,
          id: material.id,
          quantity: material.quantity || 0
        }]
      }));
    } else if (currentMaterialType === 'column') {
      setInputs(prev => ({
        ...prev,
        column: {
          height: material.materials?.column?.height?.toString() || '0',
          thickness: (material.materials?.column?.thickness || '').toString(),
          quantity: material.quantity?.toString() || '0',
          type: 'reuse'
        }
      }));
      setReuseMaterials(prev => ({
        ...prev,
        column: [{
          ...material,
          id: material.id,
          quantity: material.quantity || 0
        }]
      }));
    } else if (currentMaterialType === 'upper') {
      setInputs(prev => ({
        ...prev,
        upper: {
          width: material.materials?.upper?.width?.toString() || '0',
          thickness: (material.materials?.upper?.thickness || '').toString(),
          quantity: material.quantity?.toString() || '0',
          type: 'reuse'
        }
      }));
      setReuseMaterials(prev => ({
        ...prev,
        upper: [{
          ...material,
          id: material.id,
          quantity: material.quantity || 0
        }]
      }));
    } else if (currentMaterialType === 'lower_beam') {
      setInputs(prev => ({
        ...prev,
        lower_beam: {
          length: material.materials?.lower_beam?.length?.toString() || '0',
          thickness: (material.materials?.lower_beam?.thickness || '').toString(),
          quantity: material.quantity?.toString() || '0',
          type: 'reuse'
        }
      }));
      setReuseMaterials(prev => ({
        ...prev,
        lower_beam: [{
          ...material,
          id: material.id,
          quantity: material.quantity || 0
        }]
      }));
    } else if (currentMaterialType === 'upper_beam') {
      setInputs(prev => ({
        ...prev,
        upper_beam: {
          length: material.materials?.upper_beam?.length?.toString() || '0',
          thickness: (material.materials?.upper_beam?.thickness || '').toString(),
          quantity: material.quantity?.toString() || '0',
          type: 'reuse'
        }
      }));
      setReuseMaterials(prev => ({
        ...prev,
        upper_beam: [{
          ...material,
          id: material.id,
          quantity: material.quantity || 0
        }]
      }));
    }
    setReuseDialogOpen(false);
  };

  const handleMaterialSelect = async (material: any) => {
    try {
      console.log('선택된 자재:', material);
      
      // 자재 존재 여부 확인
      const checkResponse = await api.get(`/api/inventory/${material.id}`);
      console.log('자재 확인 응답:', checkResponse.data);
      
      if (!checkResponse.data.materials || !checkResponse.data.materials[material.name]) {
        throw new Error(`${material.name} 자재를 찾을 수 없습니다.`);
      }
      
      // 자재 수량 확인
      const quantityResponse = await api.get(`/api/inventory/${material.id}/quantity`);
      console.log('자재 수량 응답:', quantityResponse.data);
      
      if (quantityResponse.data.quantity > 0) {
        setSelectedMaterial(material);
        setQuantity(1);
      } else {
        alert('재고가 부족합니다.');
      }
    } catch (error: unknown) {
      console.error('자재 선택 중 오류:', error);
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert('자재를 선택할 수 없습니다.');
      }
    }
  };

  if (!selectedSale) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>자재 할당</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {selectedSale.purpose === '라이닝폼' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">크라운</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>크라운 타입</InputLabel>
                          <Select
                            value={inputs.crown?.type}
                            onChange={(e: SelectChangeEvent) => {
                              console.log('Crown type select changed:', e.target.value);
                              handleInputChange('crown', 'type', e.target.value as 'new' | 'reuse');
                            }}
                            label="크라운 타입"
                          >
                            <MenuItem value="new">신규</MenuItem>
                            <MenuItem value="reuse">재활용</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleReuseButtonClick('crown' as keyof ReuseMaterial['materials'])}
                          sx={{ 
                            height: '56px',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                            }
                          }}
                        >
                          재활용 자재 선택
                        </Button>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="crown_height"
                          label={!inputs.crown?.height ? "크라운 높이 (mm)" : ""}
                          value={inputs.crown?.height}
                          onChange={(e) => handleInputChange('crown', 'height', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="crown_radius"
                          label={!inputs.crown?.radius ? "크라운 반지름 (mm)" : ""}
                          value={inputs.crown?.radius}
                          onChange={(e) => handleInputChange('crown', 'radius', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="crown_quantity"
                          label={!inputs.crown?.quantity ? "크라운 수량" : ""}
                          value={inputs.crown?.quantity}
                          onChange={(e) => handleInputChange('crown', 'quantity', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                {/* 🔹 왼쪽 날개 */}
                <Grid item xs={12}>
                  <Typography variant="h6">왼쪽 날개</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>왼쪽 날개 타입</InputLabel>
                          <Select
                            value={inputs.left_wing?.type}
                            onChange={(e: SelectChangeEvent) => handleInputChange('left_wing', 'type', e.target.value as 'new' | 'reuse')}
                            label="왼쪽 날개 타입"
                          >
                            <MenuItem value="new">신규</MenuItem>
                            <MenuItem value="reuse">재활용</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleReuseButtonClick('left_wing')}
                          sx={{
                            height: '56px',
                            backgroundColor: '#1976d2',
                            '&:hover': { backgroundColor: '#1565c0' }
                          }}
                        >
                          재활용 자재 선택
                        </Button>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="left_wing_width"
                          label={!inputs.left_wing?.width ? "너비 (mm)" : ""}
                          value={inputs.left_wing?.width}
                          onChange={(e) => handleInputChange('left_wing', 'width', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="left_wing_radius"
                          label={!inputs.left_wing?.radius ? "반지름 (mm)" : ""}
                          value={inputs.left_wing?.radius}
                          onChange={(e) => handleInputChange('left_wing', 'radius', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="left_wing_quantity"
                          label={!inputs.left_wing?.quantity ? "수량" : ""}
                          value={inputs.left_wing?.quantity}
                          onChange={(e) => handleInputChange('left_wing', 'quantity', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* 🔹 오른쪽 날개 */}
                <Grid item xs={12}>
                  <Typography variant="h6">오른쪽 날개</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>오른쪽 날개 타입</InputLabel>
                          <Select
                            value={inputs.right_wing?.type}
                            onChange={(e: SelectChangeEvent) => handleInputChange('right_wing', 'type', e.target.value as 'new' | 'reuse')}
                            label="오른쪽 날개 타입"
                          >
                            <MenuItem value="new">신규</MenuItem>
                            <MenuItem value="reuse">재활용</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleReuseButtonClick('right_wing')}
                          sx={{
                            height: '56px',
                            backgroundColor: '#1976d2',
                            '&:hover': { backgroundColor: '#1565c0' }
                          }}
                        >
                          재활용 자재 선택
                        </Button>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="right_wing_width"
                          label={!inputs.right_wing?.width ? "너비 (mm)" : ""}
                          value={inputs.right_wing?.width}
                          onChange={(e) => handleInputChange('right_wing', 'width', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="right_wing_radius"
                          label={!inputs.right_wing?.radius ? "반지름 (mm)" : ""}
                          value={inputs.right_wing?.radius}
                          onChange={(e) => handleInputChange('right_wing', 'radius', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="right_wing_quantity"
                          label={!inputs.right_wing?.quantity ? "수량" : ""}
                          value={inputs.right_wing?.quantity}
                          onChange={(e) => handleInputChange('right_wing', 'quantity', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">대차기둥</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>대차기둥 타입</InputLabel>
                          <Select
                            value={inputs.column?.type}
                            onChange={(e: SelectChangeEvent) => {
                              console.log('Column type select changed:', e.target.value);
                              handleInputChange('column', 'type', e.target.value as 'new' | 'reuse');
                            }}
                            label="대차기둥 타입"
                          >
                            <MenuItem value="new">신규</MenuItem>
                            <MenuItem value="reuse">재활용</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleReuseButtonClick('column' as keyof ReuseMaterial['materials'])}
                          sx={{ 
                            height: '56px',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                            }
                          }}
                        >
                          재활용 자재 선택
                        </Button>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="column_height"
                          label={!inputs.column?.height ? "대차기둥 높이 (mm)" : ""}
                          value={inputs.column?.height}
                          onChange={(e) => handleInputChange('column', 'height', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="column_thickness"
                          label={!inputs.column?.thickness ? "대차기둥 두께 (mm)" : ""}
                          value={inputs.column?.thickness}
                          onChange={(e) => handleInputChange('column', 'thickness', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="column_quantity"
                          label={!inputs.column?.quantity ? "대차기둥 수량" : ""}
                          value={inputs.column?.quantity}
                          onChange={(e) => handleInputChange('column', 'quantity', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6">대차상부</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>대차상부 타입</InputLabel>
                          <Select
                            value={inputs.upper?.type}
                            onChange={(e: SelectChangeEvent) => {
                              console.log('Upper type select changed:', e.target.value);
                              handleInputChange('upper', 'type', e.target.value as 'new' | 'reuse');
                            }}
                            label="대차상부 타입"
                          >
                            <MenuItem value="new">신규</MenuItem>
                            <MenuItem value="reuse">재활용</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleReuseButtonClick('upper' as keyof ReuseMaterial['materials'])}
                          sx={{ 
                            height: '56px',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                            }
                          }}
                        >
                          재활용 자재 선택
                        </Button>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="upper_width"
                          label={!inputs.upper?.width ? "대차상부 너비 (mm)" : ""}
                          value={inputs.upper?.width}
                          onChange={(e) => handleInputChange('upper', 'width', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="upper_thickness"
                          label={!inputs.upper?.thickness ? "대차상부 두께 (mm)" : ""}
                          value={inputs.upper?.thickness}
                          onChange={(e) => handleInputChange('upper', 'thickness', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="upper_quantity"
                          label={!inputs.upper?.quantity ? "대차상부 수량" : ""}
                          value={inputs.upper?.quantity}
                          onChange={(e) => handleInputChange('upper', 'quantity', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6">상부 종빔</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>종빔 타입</InputLabel>
                          <Select
                            value={inputs.upper_beam?.type}
                            onChange={(e: SelectChangeEvent) => {
                              console.log('Beam type select changed:', e.target.value);
                              handleInputChange('upper_beam', 'type', e.target.value as 'new' | 'reuse');
                            }}
                            label="종빔 타입"
                          >
                            <MenuItem value="new">신규</MenuItem>
                            <MenuItem value="reuse">재활용</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleReuseButtonClick('upper_beam' as keyof ReuseMaterial['materials'])}
                          sx={{ 
                            height: '56px',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                            }
                          }}
                        >
                          재활용 자재 선택
                        </Button>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="beam_length"
                          label={!inputs.upper_beam?.length ? "상부 종빔 길이 (mm)" : ""}
                          value={inputs.upper_beam?.length}
                          onChange={(e) => handleInputChange('upper_beam', 'length', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="beam_thickness"
                          label={!inputs.upper_beam?.thickness ? "상부 종빔 두께 (mm)" : ""}
                          value={inputs.upper_beam?.thickness}
                          onChange={(e) => handleInputChange('upper_beam', 'thickness', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="beam_quantity"
                          label={!inputs.upper_beam?.quantity ? "상부 종빔 수량" : ""}
                          value={inputs.upper_beam?.quantity}
                          onChange={(e) => handleInputChange('upper_beam', 'quantity', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">하부 종빔</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel>하부 종빔 타입</InputLabel>
                          <Select
                            value={inputs.lower_beam?.type}
                            onChange={(e: SelectChangeEvent) => {
                              console.log('Beam type select changed:', e.target.value);
                              handleInputChange('lower_beam', 'type', e.target.value as 'new' | 'reuse');
                            }}
                            label="하부 종빔 타입"
                          >
                            <MenuItem value="new">신규</MenuItem>
                            <MenuItem value="reuse">재활용</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleReuseButtonClick('lower_beam' as keyof ReuseMaterial['materials'])}
                          sx={{
                            height: '56px',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                            }
                          }}
                        >
                          재활용 자재 선택
                        </Button>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          name="beam_length"
                          label={!inputs.lower_beam?.length ? "하부 종빔 길이 (mm)" : ""}
                          value={inputs.lower_beam?.length}
                          onChange={(e) => handleInputChange('lower_beam', 'length', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="beam_thickness"
                          label={!inputs.lower_beam?.thickness ? "하부 종빔 두께 (mm)" : ""}
                          value={inputs.lower_beam?.thickness}
                          onChange={(e) => handleInputChange('lower_beam', 'thickness', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          name="beam_quantity"
                          label={!inputs.lower_beam?.quantity ? "하부 종빔 수량" : ""}
                          value={inputs.lower_beam?.quantity}
                          onChange={(e) => handleInputChange('lower_beam', 'quantity', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>취소</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            할당
          </Button>
        </DialogActions>
      </Dialog>

      {/* 재활용 자재 선택 다이얼로그 */}
      <Dialog
        open={reuseDialogOpen}
        onClose={() => setReuseDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '600px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          {currentMaterialType === 'crown' ? '크라운' : 
           currentMaterialType === 'left_wing' ? '왼쪽 날개' : 
           currentMaterialType === 'right_wing' ? '오른쪽 날개' : 
           currentMaterialType === 'column' ? '대차기둥' : 
           currentMaterialType === 'upper' ? '대차상부' : 
           currentMaterialType === 'lower_beam' ? '하부 종빔' : 
           currentMaterialType === 'upper_beam' ? '상부 종빔' : 
           '종빔'} 재활용 자재 선택
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ maxHeight: 'calc(80vh - 200px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>업체명</TableCell>
                  <TableCell>현장명</TableCell>
                  <TableCell>자재명</TableCell>
                  <TableCell>높이(mm)</TableCell>
                  <TableCell>반지름(mm)</TableCell>
                  <TableCell>수량</TableCell>
                  <TableCell>선택</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reuseMaterials[currentMaterialType]?.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell>{material.company_name}</TableCell>
                    <TableCell>{material.site_name}</TableCell>
                    <TableCell>
                      {currentMaterialType === 'crown' ? '크라운' : 
                       currentMaterialType === 'left_wing' ? '왼쪽 날개' : 
                       currentMaterialType === 'right_wing' ? '오른쪽 날개' : 
                       currentMaterialType === 'column' ? '대차기둥' : 
                       currentMaterialType === 'upper' ? '대차상부' : 
                       currentMaterialType === 'lower_beam' ? '하부 종빔' : 
                       currentMaterialType === 'upper_beam' ? '상부 종빔' : 
                       '종빔'}
                    </TableCell>
                    <TableCell>
                      {currentMaterialType === 'crown' && material.materials?.crown?.height}
                      {currentMaterialType === 'left_wing' && material.materials?.left_wing?.width}
                      {currentMaterialType === 'right_wing' && material.materials?.right_wing?.width}
                      {currentMaterialType === 'column' && material.materials?.column?.height}
                      {currentMaterialType === 'upper' && material.materials?.upper?.width}
                      {currentMaterialType === 'lower_beam' && material.materials?.lower_beam?.length}
                      {currentMaterialType === 'upper_beam' && material.materials?.upper_beam?.length}
                    </TableCell>
                    <TableCell>
                      {currentMaterialType === 'crown' && material.materials?.crown?.radius}
                      {currentMaterialType === 'left_wing' && material.materials?.left_wing?.radius}
                      {currentMaterialType === 'right_wing' && material.materials?.right_wing?.radius}
                      {currentMaterialType === 'column' && material.materials?.column?.thickness}
                      {currentMaterialType === 'upper' && material.materials?.upper?.thickness}
                      {currentMaterialType === 'lower_beam' && material.materials?.lower_beam?.thickness}
                      {currentMaterialType === 'upper_beam' && material.materials?.upper_beam?.thickness}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          type="number"
                          size="small"
                          sx={{ width: '80px' }}
                          value={material.quantity || ''}
                          inputProps={{ 
                            min: 0, 
                            max: currentMaterialType === 'crown' ? material.materials?.crown?.quantity || 0 :
                                 currentMaterialType === 'left_wing' ? material.materials?.left_wing?.quantity || 0 :
                                 currentMaterialType === 'right_wing' ? material.materials?.right_wing?.quantity || 0 :
                                 currentMaterialType === 'column' ? material.materials?.column?.quantity || 0 :
                                 currentMaterialType === 'upper' ? material.materials?.upper?.quantity || 0 :
                                 currentMaterialType === 'lower_beam' ? material.materials?.lower_beam?.quantity || 0 :
                                 currentMaterialType === 'upper_beam' ? material.materials?.upper_beam?.quantity || 0 :
                                 0 
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              setReuseMaterials(prev => ({
                                ...prev,
                                [currentMaterialType]: prev[currentMaterialType].map(item => 
                                  item.id === material.id ? { ...item, quantity: 0 } : item
                                )
                              }));
                              return;
                            }
                            
                            const newQuantity = parseInt(value);
                            const maxQuantity = currentMaterialType === 'crown' ? material.materials?.crown?.quantity || 0 :
                                              currentMaterialType === 'left_wing' ? material.materials?.left_wing?.quantity || 0 :
                                              currentMaterialType === 'right_wing' ? material.materials?.right_wing?.quantity || 0 :
                                              currentMaterialType === 'column' ? material.materials?.column?.quantity || 0 :
                                              currentMaterialType === 'upper' ? material.materials?.upper?.quantity || 0 :
                                              currentMaterialType === 'lower_beam' ? material.materials?.lower_beam?.quantity || 0 :
                                              currentMaterialType === 'upper_beam' ? material.materials?.upper_beam?.quantity || 0 :
                                              0;
                            
                            if (newQuantity >= 0 && newQuantity <= maxQuantity) {
                              setReuseMaterials(prev => ({
                                ...prev,
                                [currentMaterialType]: prev[currentMaterialType].map(item => 
                                  item.id === material.id ? { ...item, quantity: newQuantity } : item
                                )
                              }));
                            }
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          / {currentMaterialType === 'crown' ? material.materials?.crown?.quantity :
                             currentMaterialType === 'left_wing' ? material.materials?.left_wing?.quantity :
                             currentMaterialType === 'right_wing' ? material.materials?.right_wing?.quantity :
                             currentMaterialType === 'column' ? material.materials?.column?.quantity :
                             currentMaterialType === 'upper' ? material.materials?.upper?.quantity :
                             currentMaterialType === 'lower_beam' ? material.materials?.lower_beam?.quantity :
                             currentMaterialType === 'upper_beam' ? material.materials?.upper_beam?.quantity :
                             0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleReuseMaterialSelect(material)}
                        sx={{ 
                          backgroundColor: '#1976d2',
                          '&:hover': {
                            backgroundColor: '#1565c0',
                          }
                        }}
                      >
                        선택
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReuseDialogOpen(false)}
            sx={{ 
              backgroundColor: '#f5f5f5',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              }
            }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
} 
 
 
 